import { useEventOffsetNote } from 'pages/search-transient-event-monthly/components/search-controls-event/EventOffsetNote';
import StorageUtils from '@spothero/utils/storage';
import { isOversizedFeatureEnabled } from 'utils/search-vehicle';
import { useSelector, useDispatch } from 'react-redux';
import { getParkingType } from 'store/selectors/selectors';
import { setSearchEventPopoverVisible, setSearchLoyaltyProgramPopoverVisible, } from 'store/search/search-actions';
import { useVehicleTooltipText } from 'pages/search-transient-event-monthly/components/search-controls/VehiclePopover';
import { StorageTypes, StorageKeys } from 'common/constants/storageKeys';
import { LOYALTY_PROGRAM_FILTER_VALUES } from 'store/filters/loyalty-filter-utils';
export const TOOLTIPS = {
    event_offset_popover: 'event_offset_popover',
    search_vehicle_popover: 'search_vehicle_popover',
    loyalty_program_popover: 'loyalty_program_popover',
};
const useTooltipConfigs = () => {
    const dispatch = useDispatch();
    const parkingType = useSelector(getParkingType);
    const isOversizedEnabled = isOversizedFeatureEnabled(parkingType);
    const { text: vehicleText, handleClose: handleVehicleTooltipClose, } = useVehicleTooltipText({
        isOversizedEnabled,
    });
    const { text: eventText, hasOffsets: hasEventOffsets } = useEventOffsetNote({
        showActions: true,
    });
    // Loyalty program logic from FilterLoyaltyProgram component
    const user = useSelector((s) => s.user.data);
    const loyaltyProgramSelectedValue = useSelector((s) => s.filters.loyaltyProgram);
    const loyaltyProgramIsSelected = loyaltyProgramSelectedValue === LOYALTY_PROGRAM_FILTER_VALUES.ON;
    const loyaltyProgramAutoOff = loyaltyProgramSelectedValue ===
        LOYALTY_PROGRAM_FILTER_VALUES.AUTOMATIC_OFF;
    const seenLoyaltyProgramInfo = StorageUtils.get(StorageKeys.SEEN_LOYALTY_PROGRAM_INFO_COOKIE, StorageTypes.COOKIE);
    return [
        // Index 0 = highest priority
        {
            key: TOOLTIPS.loyalty_program_popover,
            getText: () => {
                if (!user?.operatorLoyaltyProgram) {
                    return { text: null, isEnabled: false };
                }
                const program = user.operatorLoyaltyProgram;
                const text = loyaltyProgramAutoOff
                    ? `There are no ${program.title} spots in the area, so we're showing you all options nearby.`
                    : `Results show only facilities that accept your ${program.title} credit. Tap the button to view all facilities.`;
                // Show if auto-off OR if user selected and hasn't seen info
                const shouldShow = loyaltyProgramAutoOff ||
                    (user?.operatorLoyaltyProgram &&
                        loyaltyProgramIsSelected &&
                        !seenLoyaltyProgramInfo);
                return {
                    text,
                    isEnabled: shouldShow,
                };
            },
            onClose: () => dispatch(setSearchLoyaltyProgramPopoverVisible(false)),
            reduxStateSelector: (state) => state.search.data.searchLoyaltyProgramPopoverVisible || false,
        },
        {
            key: TOOLTIPS.event_offset_popover,
            getText: () => ({
                text: eventText,
                isEnabled: hasEventOffsets,
            }),
            onClose: () => dispatch(setSearchEventPopoverVisible(false)),
            reduxStateSelector: (state) => state.search.data.searchEventOffsetPopoverVisible,
        },
        {
            key: TOOLTIPS.search_vehicle_popover,
            getText: () => ({
                text: vehicleText,
                isEnabled: (vehicleText || '').length > 0,
            }),
            onClose: handleVehicleTooltipClose,
            reduxStateSelector: (state) => state.search.data.searchOversizePopoverVisible,
        },
    ];
};
const dummyFunc = () => false;
export const useTooltips = (tooltip) => {
    const tooltipConfigs = useTooltipConfigs();
    const eventsPopupOpen = useSelector((state) => state.search.data.eventsPopupOpen);
    let targetConfig;
    let targetIndex = -1;
    tooltipConfigs.find((config, index) => {
        if (config.key === tooltip) {
            targetConfig = config;
            targetIndex = index;
            return true;
        }
        return false;
    });
    // Subscribe to redux state changes for re-renders
    useSelector(targetConfig?.reduxStateSelector || dummyFunc);
    const handleTooltipClose = () => {
        StorageUtils.set(`${tooltip}_visible`, false, StorageTypes.SESSION);
        targetConfig?.onClose();
    };
    if (!targetConfig) {
        return {
            isVisible: false,
            text: null,
            handleClose: handleTooltipClose,
        };
    }
    const { text, isEnabled } = targetConfig.getText();
    const sessionStorageKey = `${tooltip}_visible`;
    const sessionIsVisible = StorageUtils.get(sessionStorageKey, StorageTypes.SESSION) !== false;
    // Check if any higher priority tooltips (lower index) should be shown instead
    const higherPriorityVisible = tooltipConfigs
        .slice(0, targetIndex) // Only check tooltips with higher priority (lower index)
        .some(config => {
        const { text: configText, isEnabled: configEnabled, } = config.getText();
        const configSessionVisible = StorageUtils.get(`${config.key}_visible`, StorageTypes.SESSION) !== false;
        return (configSessionVisible &&
            configEnabled &&
            (configText || '').length > 0);
    });
    const isVisible = !eventsPopupOpen &&
        !higherPriorityVisible &&
        sessionIsVisible &&
        isEnabled &&
        (text || '').length > 0;
    return {
        isVisible,
        text,
        handleClose: handleTooltipClose,
    };
};
